import { z } from 'zod';

const trendPointSchema = z.object({
  mes: z.string(),
  clientes: z.coerce.number(),
  imoveis: z.coerce.number(),
  processos: z.coerce.number(),
  hectares: z.coerce.number(),
});

const carteiraSchema = z.object({
  clientes_ativos: z.coerce.number(),
  imoveis: z.coerce.number(),
  processos_em_andamento: z.coerce.number(),
  hectares_sob_gestao: z.coerce.number(),
  total_diagnosticos: z.coerce.number(),
  trend_12m: z.array(trendPointSchema),
});

const volumeMesSchema = z.object({
  abertos: z.coerce.number(),
  concluidos: z.coerce.number(),
  taxa_conclusao_pct: z.coerce.number().nullable(),
  comparativo: z.object({
    abertos: z.coerce.number(),
    concluidos: z.coerce.number(),
    taxa_conclusao_pct: z.coerce.number().nullable(),
  }),
});

const eficienciaSchema = z.object({
  tempo_medio_ciclo_dias: z.coerce.number().nullable(),
  tempo_medio_ciclo_dias_anterior: z.coerce.number().nullable(),
  funil: z.array(
    z.object({
      coluna: z.coerce.number(),
      processos: z.coerce.number(),
      media_dias: z.coerce.number(),
    }),
  ),
});

const mixSchema = z.object({
  por_tipo: z.array(
    z.object({
      tipo: z.string(),
      count: z.coerce.number(),
      pct: z.coerce.number(),
    }),
  ),
  por_cidade: z.array(
    z.object({
      cidade: z.string(),
      count: z.coerce.number(),
      hectares: z.coerce.number(),
    }),
  ),
});

export const hectaresRankingItemSchema = z.object({
  tipo: z.enum(['cliente', 'grupo', 'sem_vinculo']),
  id: z.string(),
  nome: z.string(),
  hectares: z.coerce.number(),
});
export type HectaresRankingItem = z.infer<typeof hectaresRankingItemSchema>;
export const hectaresRankingSchema = z.array(hectaresRankingItemSchema);

// Clientes (proprietários) com imóveis numa cidade — modal do gráfico "Por cidade".
export const cidadeClienteSchema = z.object({
  cpf: z.string(),
  nome: z.string(),
  imoveis: z.coerce.number(),
  hectares: z.coerce.number(),
});
export type CidadeCliente = z.infer<typeof cidadeClienteSchema>;
export const cidadeClientesSchema = z.array(cidadeClienteSchema);

// Imóveis com proprietário — modal do card "Imóveis sob gestão".
export const imovelGestaoSchema = z.object({
  imovel_id: z.coerce.number(),
  denominacao: z.string(),
  municipio: z.string().nullable(),
  uf: z.string().nullable(),
  matricula: z.string().nullable(),
  hectares: z.coerce.number(),
  cliente_cpf: z.string().nullable(),
  cliente_nome: z.string().nullable(),
});
export type ImovelGestao = z.infer<typeof imovelGestaoSchema>;
export const imoveisGestaoSchema = z.array(imovelGestaoSchema);

const operacaoAreaSchema = z.object({
  dominio: z.string(),
  label: z.string(),
  em_andamento: z.coerce.number(),
  concluidos_mes: z.coerce.number(),
  atrasados: z.coerce.number(),
});

const alertaSchema = z.object({
  severidade: z.enum(['danger', 'warning', 'info']),
  texto: z.string(),
  contexto: z.string(),
});
export type DashAlerta = z.infer<typeof alertaSchema>;
export type OperacaoArea = z.infer<typeof operacaoAreaSchema>;

export const dashboardExecutiveSchema = z.object({
  carteira: carteiraSchema,
  volume_mes: volumeMesSchema,
  eficiencia: eficienciaSchema,
  mix: mixSchema,
  operacao_por_area: z.array(operacaoAreaSchema).default([]),
  alertas: z.array(alertaSchema).default([]),
  ts: z.string(),
});
export type DashboardExecutive = z.infer<typeof dashboardExecutiveSchema>;
export type TrendPoint = z.infer<typeof trendPointSchema>;

// Labels para colunas kanban GEO/CCIR.
export const COLUNA_LABEL: Record<number, string> = {
  1: 'Pendente',
  2: 'Campo',
  3: 'Montagem pasta',
  4: 'Aguard. SIGEF',
  5: 'Em andamento',
  6: 'Aguard. cartório',
  7: 'Concluído',
  10: 'CCIR em andamento',
  11: 'CCIR em análise',
};

export const TIPO_LABEL: Record<string, string> = {
  georreferenciamento: 'Georreferenciamento',
  titulacao: 'Titulação',
  ccir: 'CCIR',
  ambiental_car: 'Ambiental · CAR',
  sem_tipo: 'Sem tipo',
};

export function tipoLabel(tipo: string): string {
  return TIPO_LABEL[tipo] ?? tipo.replace(/_/g, ' ');
}
